import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import { LineChartComponent } from '../../chart-components/LineChartComponent.jsx';
import useWidgetData from '../hooks/useWidgetData';
import { transformWidgetData } from '../widgetConfigSchema';
import { useFilterStoreWithMomentDates } from '../../../store/filterStore.js';

/**
 * ResourceChangesTrend Widget - Wraps the LineChartComponent with widget functionality
 * Shows resource changes trend over time with support for both static and API data sources
 */
const ResourceChangesTrendWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false
}) => {
  const { startDate, endDate } = useFilterStoreWithMomentDates();

  // Stable filters to avoid re-fetch loops when filters is undefined or recreated
  const stableFilters = useMemo(() => filters || [], [filters]);

  // Build a non-mutating config with updated dates merged into the request body; memoize to keep reference stable
  const finalConfig = useMemo(() => {
    const dataSource = config?.dataSource || {};
    const baseBody = dataSource.body || {};
    
    // Only add dates if both are present to avoid calling .format on null/undefined
    const bodyWithDates = (startDate && endDate) ? {
      ...baseBody,
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    } : baseBody;

    return {
      ...config,
      dataSource: {
        ...dataSource,
        method: dataSource.method || 'POST',
        body: bodyWithDates
      }
    };
  }, [config, startDate, endDate]);

  const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);

  // Transform data if it comes from API
  const widgetData = data ? transformWidgetData('resourcechangestrend', data) : null;

  return (
    <BaseWidget
      widgetId={widgetId}
      title={config.title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      <div 
        className="h-full flex flex-col"
        style={{ minHeight: '200px', height: '100%' }}
      >
        {widgetData && widgetData.data ? (
          <div className="flex-1 flex flex-col h-full">
            <LineChartComponent
              data={widgetData.data}
              title={config.title}
              useOwnBackground={false}
            />
          </div>
        ) : (
          <div 
            className="flex-1 flex items-center justify-center"
            role="status"
            aria-live="polite"
          >
            <p className="text-text-2 text-sm">
              {!widgetData ? 'Unsupported data format' : 'No data available'}
            </p>
          </div>
        )}
      </div>
    </BaseWidget>
  );
};

ResourceChangesTrendWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool
};

export default ResourceChangesTrendWidget;

